import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CoffeeChatLayout from '../../components/coffee-chat/CoffeeChatLayout';
import { fetchLecturers, Lecturer } from '../../services/lecturerApi';
import { getLecturerImageWithFallback } from '../../utils/imageUtils';
import { searchLecturers, LecturerSearchItem } from '../../services/searchApi';

// 커피챗 카드 타입 (Lecturer 인터페이스 활용)
interface CoffeeChatCard {
  id: string;
  category: string;
  title: string;
  description: string;
  imageUrl: string;
  tags?: string[];
}

const CoffeeChatHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState('');
  const [horizontalPage, setHorizontalPage] = useState(1); // 가로 슬라이드 페이지
  const totalHorizontalPages = 3; // 가로 슬라이드 총 3페이지
  
  // 강연자 데이터 상태 관리
  const [connectWorldLecturers, setConnectWorldLecturers] = useState<Lecturer[]>([]);
  const [allLecturers, setAllLecturers] = useState<Lecturer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 검색 관련 상태
  const [searchResults, setSearchResults] = useState<LecturerSearchItem[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  
  // 무한스크롤 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const PAGE_SIZE = 16;
  
  // 이미지 로딩 상태 관리
  const [imageLoadStates, setImageLoadStates] = useState<{[key: string]: boolean}>({});

  // 가로 스크롤 드래그 상태 관리
  const [horizontalDrag, setHorizontalDrag] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    offset: 0
  });

  // 컨테이너 참조
  const horizontalRef = useRef<HTMLDivElement>(null);

  // 강연자 데이터 API 호출
  useEffect(() => {
    const loadLecturers = async () => {
      try {
        setIsLoading(true);
        
        // CONNECT TO THE WORLD 섹션용 - page=0, size=12 (페이징을 위해 12개 필요)
        const connectWorldResponse = await fetchLecturers({ page: 0, size: 12 });
        setConnectWorldLecturers(connectWorldResponse.data.content);
        
        // 첫 페이지 전체 출력용 - page=0, size=16
        await loadAllLecturers(0, false);
        
      } catch (error) {
        console.error('강연자 데이터 로딩 실패:', error);
        // 에러 발생 시 빈 배열로 설정
        setConnectWorldLecturers([]);
        setAllLecturers([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (!isSearchMode) {
      loadLecturers();
    }
  }, [isSearchMode]);

  // URL에서 검색 키워드 확인 및 검색 실행
  useEffect(() => {
    const keyword = searchParams.get('keyword');
    if (keyword && keyword.trim()) {
      setSearchValue(keyword);
      setIsSearchMode(true);
      performSearch(keyword);
    } else {
      setIsSearchMode(false);
      setSearchResults([]);
    }
  }, [searchParams]);

  // 검색 실행 함수
  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) return;

    setSearchLoading(true);
    setSearchError(null);

    try {
      const results = await searchLecturers(keyword);
      setSearchResults(results);
    } catch (error) {
      console.error('강연자 검색 오류:', error);
      setSearchError('검색 중 오류가 발생했습니다.');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 전체 강연자 데이터 로드 함수 (무한스크롤용)
  const loadAllLecturers = async (page: number, isLoadMore: boolean = false) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      }
      
      const response = await fetchLecturers({ page, size: PAGE_SIZE });
      const lecturers = response.data.content;
      
      if (isLoadMore) {
        // 더 로드하는 경우 기존 데이터에 추가
        setAllLecturers(prevData => [...prevData, ...lecturers]);
      } else {
        // 초기 로드인 경우 새 데이터로 설정
        setAllLecturers(lecturers);
      }
      
      // 더 로드할 데이터가 있는지 확인
      const totalPages = response.data.totalPages;
      setHasMoreData(page + 1 < totalPages);
      setCurrentPage(page);
      
    } catch (error) {
      console.error('전체 강연자 데이터 로딩 실패:', error);
    } finally {
      if (isLoadMore) {
        setIsLoadingMore(false);
      }
    }
  };

  // 강연자 데이터를 카드 형태로 변환
  const lecturerToCard = (lecturer: Lecturer): CoffeeChatCard => ({
    id: lecturer.id.toString(),
    category: '강연자',
    tags: lecturer.tags || [],
    title: lecturer.name,
    description: lecturer.introduction,
    imageUrl: getLecturerImageWithFallback(lecturer.image_url, lecturer.id)
  });

  // 검색 결과 강연자 데이터를 카드 형태로 변환
  const searchLecturerToCard = (lecturer: LecturerSearchItem): CoffeeChatCard => ({
    id: lecturer.id.toString(),
    category: '강연자',
    tags: [], // 검색 결과에는 태그 정보가 없음
    title: lecturer.name,
    description: lecturer.introduction || lecturer.medicine || '강연자',
    imageUrl: getLecturerImageWithFallback(lecturer.image_url, lecturer.id)
  });

  // 가로 슬라이드 데이터 (실제 강연자 데이터 활용)
  const getHorizontalData = () => {
    if (connectWorldLecturers.length === 0) {
      return { 1: [], 2: [], 3: [] };
    }
    
    // 12개의 강연자를 3페이지로 분할
    const cards = connectWorldLecturers.map(lecturerToCard);
    
    return {
      1: cards.slice(0, 4),   // 첫 번째 페이지: 0-3 (4개)
      2: cards.slice(4, 8),   // 두 번째 페이지: 4-7 (4개)
      3: cards.slice(8, 12)   // 세 번째 페이지: 8-11 (4개)
    };
  };
  
  const horizontalData = getHorizontalData();

  // 전체 카드 데이터 (검색 모드에 따라 다른 데이터 사용)
  const allCardsData = isSearchMode 
    ? searchResults.map(searchLecturerToCard)
    : allLecturers.map(lecturerToCard);

  // 이미지 로드 핸들러
  const handleImageLoad = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: true
    }));
  };

  // 이미지 에러 핸들러
  const handleImageError = (imageKey: string) => {
    setImageLoadStates(prev => ({
      ...prev,
      [imageKey]: false
    }));
  };

  const handleCardClick = (cardId: string) => {
    // 강연자 ID를 기반으로 상세 페이지로 이동
    navigate(`/coffee-chat/${cardId}`);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const searchTerm = (e.target as HTMLInputElement).value.trim();
      if (searchTerm) {
        // URL 파라미터 변경하여 검색 실행
        navigate(`/coffee-chat?keyword=${encodeURIComponent(searchTerm)}`);
      } else {
        // 검색어가 비어있으면 기본 페이지로 이동
        navigate('/coffee-chat');
      }
    }
  };

  const handleHorizontalPageChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && horizontalPage > 1) {
      setHorizontalPage(horizontalPage - 1);
    } else if (direction === 'next' && horizontalPage < totalHorizontalPages) {
      setHorizontalPage(horizontalPage + 1);
    }
  };

  // 가로 스크롤 드래그 핸들러들
  const handleHorizontalDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setHorizontalDrag({
      isDragging: true,
      startX: clientX,
      currentX: clientX,
      offset: 0
    });
  }, []);

  const handleHorizontalDragMove = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    if (horizontalDrag.isDragging) {
      const offset = clientX - horizontalDrag.startX;
      setHorizontalDrag(prev => ({
        ...prev,
        currentX: clientX,
        offset: offset
      }));
    }
  }, [horizontalDrag.isDragging, horizontalDrag.startX]);

  const handleHorizontalDragEnd = useCallback(() => {
    if (horizontalDrag.isDragging) {
      const dragDistance = horizontalDrag.currentX - horizontalDrag.startX;
      const threshold = 100;
      
      if (dragDistance > threshold && horizontalPage > 1) {
        setHorizontalPage(horizontalPage - 1);
      } else if (dragDistance < -threshold && horizontalPage < totalHorizontalPages) {
        setHorizontalPage(horizontalPage + 1);
      }
      
      setHorizontalDrag({
        isDragging: false,
        startX: 0,
        currentX: 0,
        offset: 0
      });
    }
  }, [horizontalDrag, horizontalPage]);

  // 무한스크롤 구현
  useEffect(() => {
    const handleScroll = () => {
      // 페이지 하단에서 200px 위에 도달하면 더 로드
      if (
        window.innerHeight + document.documentElement.scrollTop 
        >= document.documentElement.offsetHeight - 200
      ) {
        if (!isLoadingMore && hasMoreData) {
          loadAllLecturers(currentPage + 1, true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage, hasMoreData, isLoadingMore]);

  return (
    <CoffeeChatLayout>
      <Container>
        <MainContainer>
          {/* 상단 배너 이미지 */}
          <BannerImage>
            {!imageLoadStates['banner'] && (
              <BannerImageSkeleton />
            )}
            <img 
              src="/images/ccf.png" 
              alt="커피챗 배너"
              style={{ 
                display: imageLoadStates['banner'] ? 'block' : 'none' 
              }}
              onLoad={() => handleImageLoad('banner')}
              onError={() => handleImageError('banner')}
            />
          </BannerImage>

          {/* 브레드크럼과 검색바 - 상단 썸네일과 32px 여백 */}
          <NavigationBar>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <ChevronIcon>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </ChevronIcon>
              <BreadcrumbItem>커피챗</BreadcrumbItem>
            </BreadcrumbContainer>
            
            <SearchContainer>
              <SearchBox>
                <SearchIcon>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path 
                      d="M14.87 14.87L10.4 10.4M12 6.5C12 9.54 9.54 12 6.5 12C3.46 12 1 9.54 1 6.5C1 3.46 3.46 1 6.5 1C9.54 1 12 3.46 12 6.5Z" 
                      stroke="rgba(55, 56, 60, 0.28)" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchIcon>
                <SearchInput 
                  type="text" 
                  placeholder="검색어를 입력하세요."
                  value={searchValue}
                  onChange={handleSearchChange}
                  onKeyUp={handleSearchSubmit}
                />
              </SearchBox>
            </SearchContainer>
          </NavigationBar>

          {/* CONNECT TO THE WORLD 헤더 + 페이징 - 검색 모드가 아닐 때만 표시 */}
          {!isSearchMode && (
            <>
              <HeaderSection>
                <HeaderContent>
                  <SectionTitle>CONNECT TO THE WORLD</SectionTitle>
                </HeaderContent>
                <PaginationButtons>
                  <PaginationContainer>
                    <PaginationContent>
                      <PaginationBtn 
                        onClick={() => handleHorizontalPageChange('prev')}
                        disabled={horizontalPage === 1}
                      >
                        <PaginationIcon isActive={horizontalPage > 1}>
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path 
                              d="M11.55 3.45L6.45 8.55L11.55 13.65" 
                              stroke={horizontalPage > 1 ? "rgba(55, 56, 60, 0.61)" : "rgba(55, 56, 60, 0.28)"} 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </PaginationIcon>
                      </PaginationBtn>
                      <PaginationBtn 
                        onClick={() => handleHorizontalPageChange('next')}
                        disabled={horizontalPage === totalHorizontalPages}
                      >
                        <PaginationIcon isRight isActive={horizontalPage < totalHorizontalPages}>
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path 
                              d="M6.45 3.45L11.55 8.55L6.45 13.65" 
                              stroke={horizontalPage < totalHorizontalPages ? "rgba(55, 56, 60, 0.61)" : "rgba(55, 56, 60, 0.28)"} 
                              strokeWidth="1.5" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </PaginationIcon>
                      </PaginationBtn>
                    </PaginationContent>
                  </PaginationContainer>
                </PaginationButtons>
              </HeaderSection>

              {/* 가로 슬라이드 카드 섹션 */}
              <HorizontalSlideSection>
            <SlideContainer
              ref={horizontalRef}
              onMouseDown={handleHorizontalDragStart}
              onMouseMove={handleHorizontalDragMove}
              onTouchStart={handleHorizontalDragStart}
              onTouchMove={handleHorizontalDragMove}
              onMouseUp={handleHorizontalDragEnd}
              onTouchEnd={handleHorizontalDragEnd}
            >
              <SlideWrapper style={{ 
                transform: `translateX(calc(-${(horizontalPage - 1) * (100/3)}% + ${horizontalDrag.isDragging ? horizontalDrag.offset : 0}px))`,
                transition: horizontalDrag.isDragging ? 'none' : 'transform 0.6s ease'
              }}>
                {Object.entries(horizontalData).map(([pageNum, cards]) => (
                  <SlidePage key={pageNum}>
                    <HorizontalCardGrid>
                      {cards.length > 0 ? cards.map((card) => (
                        <HorizontalCoffeeChatCard 
                          key={card.id}
                          onClick={() => handleCardClick(card.id)}
                        >
                          <HorizontalCardImageContainer>
                            {!imageLoadStates[`horizontal-${card.id}`] && (
                              <HorizontalCardImageSkeleton />
                            )}
                            <HorizontalCardImage 
                              src={card.imageUrl} 
                              alt={card.title}
                              style={{ 
                                display: imageLoadStates[`horizontal-${card.id}`] ? 'block' : 'none' 
                              }}
                              onLoad={() => handleImageLoad(`horizontal-${card.id}`)}
                              onError={() => handleImageError(`horizontal-${card.id}`)}
                            />
                          </HorizontalCardImageContainer>
                          <HorizontalCardContent>
                            <BadgeContainer>
                              {card?.tags?.map((tag) => (
                                <HorizontalCardBadge>{tag}</HorizontalCardBadge>
                              ))}
                            </BadgeContainer>
                            <CardTextContent>
                              <HorizontalCardTitle>{card.title}</HorizontalCardTitle>
                              <HorizontalCardCaption>{card.description}</HorizontalCardCaption>
                            </CardTextContent>
                          </HorizontalCardContent>
                        </HorizontalCoffeeChatCard>
                      )) : (
                        // 로딩 중이거나 데이터가 없을 때 스켈레톤 UI
                        Array.from({ length: 4 }, (_, index) => (
                          <HorizontalCoffeeChatCard key={`skeleton-${pageNum}-${index}`}>
                            <HorizontalCardImageContainer>
                              <HorizontalCardImageSkeleton />
                            </HorizontalCardImageContainer>
                            <HorizontalCardContent>
                              <BadgeContainer>
                                <HorizontalCardBadgeLoader />
                              </BadgeContainer>
                              <CardTextContent>
                                <HorizontalCardTitleLoader />
                                <HorizontalCardCaptionLoader />
                              </CardTextContent>
                            </HorizontalCardContent>
                          </HorizontalCoffeeChatCard>
                        ))
                      )}
                    </HorizontalCardGrid>
                  </SlidePage>
                ))}
              </SlideWrapper>
            </SlideContainer>
          </HorizontalSlideSection>

              {/* 구분선 - 헤더와의 여백 없음 */}
              <Divider />
            </>
          )}

          {/* 검색 모드일 때 제목 표시 */}
          {isSearchMode && (
            <SearchResultHeader>
              <SearchResultTitle>'{searchValue}' 검색 결과</SearchResultTitle>
            </SearchResultHeader>
          )}

          {/* 카드 그리드 섹션들 - 구분선과의 여백 없음 */}
          <CardSection>
            {isSearchMode && searchLoading ? (
              <LoadingState>검색 중...</LoadingState>
            ) : isSearchMode && searchError ? (
              <EmptyState>{searchError}</EmptyState>
            ) : isSearchMode && searchResults.length === 0 ? (
              <EmptyState>'{searchValue}'에 대한 검색 결과가 없습니다.</EmptyState>
            ) : (
              <CardGrid>
                {allCardsData.length > 0 ? allCardsData.map((card) => (
                  <Card 
                    key={card.id}
                    onClick={() => handleCardClick(card.id)}
                  >
                    <CardImage>
                      {!imageLoadStates[`card-${card.id}`] && (
                        <CardImageSkeleton />
                      )}
                      <img 
                        src={card.imageUrl} 
                        alt={card.title}
                        style={{ 
                          display: imageLoadStates[`card-${card.id}`] ? 'block' : 'none' 
                        }}
                        onLoad={() => handleImageLoad(`card-${card.id}`)}
                        onError={() => handleImageError(`card-${card.id}`)}
                      />
                    </CardImage>
                    <CardContent>
                      <BadgeContainer>
                        {card?.tags?.map((tag) => (
                          <Badge key={tag}>{tag}</Badge>
                        ))}
                      </BadgeContainer>
                      <CardTextContent>
                        <CardTitle>{card.title}</CardTitle>
                        <CardDescription>{card.description}</CardDescription>
                      </CardTextContent>
                    </CardContent>
                  </Card>
                )) : (
                  // 로딩 중이거나 데이터가 없을 때 스켈레톤 UI
                  Array.from({ length: 16 }, (_, index) => (
                    <Card key={`skeleton-${index}`}>
                      <CardImage>
                        <CardImageSkeleton />
                      </CardImage>
                      <CardContent>
                        <BadgeContainer>
                          <BadgeLoader />
                        </BadgeContainer>
                        <CardTextContent>
                          <CardTitleLoader />
                          <CardDescriptionLoader />
                        </CardTextContent>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardGrid>
            )}
            
            {/* 무한스크롤 로딩 인디케이터 - 검색 모드가 아닐 때만 표시 */}
            {!isSearchMode && isLoadingMore && (
              <LoadMoreIndicator>
                <div>더 많은 강연자를 불러오는 중...</div>
              </LoadMoreIndicator>
            )}
            
            {!isSearchMode && !hasMoreData && allCardsData.length > 0 && (
              <div style={{ 
                textAlign: 'center', 
                color: 'rgba(55, 56, 60, 0.61)', 
                fontSize: '16px',
                padding: '40px 0'
              }}>
                모든 커피챗을 확인했습니다
              </div>
            )}
          </CardSection>
        </MainContainer>
      </Container>
    </CoffeeChatLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

// 가로 슬라이드 섹션 스타일 (메인페이지와 동일)
const HorizontalSlideSection = styled.div`
  width: 100%;
  margin-bottom: 0;
`;

const SlideContainer = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  user-select: none;
  
  &:active {
    cursor: grabbing;
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  width: 300%; /* 3페이지 * 100% */
  transition: transform 0.6s ease;
  will-change: transform;
`;

const SlidePage = styled.div`
  width: calc(100% / 3); /* 전체 너비의 1/3 */
  flex-shrink: 0;
`;

const HorizontalCardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

// 커피챗 카드 컴포넌트 (가로 스크롤용)
const HorizontalCoffeeChatCard = styled.div`
  flex-direction: column;
  gap: 12px;
  display: flex;
  cursor: pointer;
  width: 282px; // 고정 너비
  flex-shrink: 0; // 축소 방지
`;

const HorizontalCardImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  min-height: 188px; /* 최소 높이 보장 */
`;

const HorizontalCardImage = styled.img`
  width: 282px;
  height: 188px;
  position: absolute;
  top: 0;
  left: 0;
  object-fit: cover;
`;

const HorizontalCardBadge = styled.div`
  height: 20px;
  padding: 0 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP';
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
`;

const HorizontalCardContent = styled.div`
  flex-direction: column;
  gap: 4px;
  display: flex;
`;

const HorizontalCardTitle = styled.div`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP';
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const HorizontalCardCaption = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP';
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  padding-top: 24px;
  padding-bottom: 128px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  /* 각 섹션별 간격 조정을 위해 자식 요소에서 개별 margin 설정 */
`;

const BannerImage = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  min-height: 400px; /* 최소 높이 보장 */
  
  img {
    width: 100%;
    height: 400px;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
  }
`;

const NavigationBar = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: auto; /* height를 auto로 변경하여 내용에 맞게 조정 */
  min-height: 32px;
  gap: 8px;
  margin-top: 32px; /* 배너와의 여백 추가 */
  margin-bottom: 24px; /* 다음 섹션과의 여백 추가 */
  z-index: 10;
  position: relative;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchContainer = styled.div`
  width: 282px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SearchBox = styled.div`
  width: 100%;
  padding: 8px;
  background: white;
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  outline: 1px solid rgba(112, 115, 124, 0.22);
  outline-offset: -1px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const SearchIcon = styled.div`
  width: 16px;
  height: 16px;
  padding: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  color: rgba(55, 56, 60, 0.28);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16.01px;
  letter-spacing: 0.30px;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
`;

const HeaderSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-top: 32px; /* 브레드크럼과의 여백 */
`;

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;
`;

const PaginationButtons = styled.div`
  height: 38px;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  display: inline-flex;
`;

const PaginationContainer = styled.div`
  position: relative;
  border-radius: 10px;
  outline: 1px rgba(112, 115, 124, 0.16) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationContent = styled.div`
  overflow: hidden;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const PaginationBtn = styled.button<{ disabled?: boolean }>`
  padding: 7px;
  justify-content: center;
  align-items: center;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  
  &:disabled {
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(23, 23, 25, 0.04);
      border-radius: 6px;
    }
  }
  
  &:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }
  
  &:last-child {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
`;

const PaginationIcon = styled.div<{ isRight?: boolean; isActive?: boolean }>`
  height: 18px;
  width: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity 0.2s ease;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
  margin-top: 24px; /* 헤더와의 여백 */
`;

const CardSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  
  &:first-of-type {
    margin-top: 24px; /* 구분선과의 여백 */
  }
`;

const CardGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 24px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(6, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(8, 1fr);
    gap: 16px;
  }
`;

const Card = styled.div`
  flex: 1;
  max-width: 282px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  cursor: pointer;
`;

const CardImage = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  min-height: 188px; /* 최소 높이 보장 */
  
  img {
    width: 100%;
    height: 188px;
    position: absolute;
    top: 0;
    left: 0;
    object-fit: cover;
  }
`;

const CardContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BadgeContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
  align-items: flex-start;
`;

const Badge = styled.div`
  height: 20px;
  padding: 0 6px;
  background: rgba(68, 129, 129, 0.08);
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  
  color: var(--Primary-Normal, #448181);
  font-size: 11px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.34px;
`;

const CardTextContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.div`
  width: 100%;
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const CardDescription = styled.div`
  width: 100%;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

// 스켈레톤 UI 컴포넌트들
const BannerImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CardImageSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 가로 슬라이드 카드 텍스트 스켈레톤
const HorizontalCardBadgeLoader = styled.div`
  height: 20px;
  width: 50px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardTitleLoader = styled.div`
  height: 24px;
  width: 80%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const HorizontalCardCaptionLoader = styled.div`
  height: 18px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// 메인 카드 텍스트 스켈레톤
const BadgeLoader = styled.div`
  height: 20px;
  width: 50px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 6px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CardTitleLoader = styled.div`
  height: 24px;
  width: 75%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const CardDescriptionLoader = styled.div`
  height: 18px;
  width: 90%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  
  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

/* 무한스크롤 로딩 인디케이터 */
const LoadMoreIndicator = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

/* 검색 결과 헤더 */
const SearchResultHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 32px;
`;

const SearchResultTitle = styled.h2`
  color: #171719;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;
`;

/* 로딩 상태 */
const LoadingState = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

/* 빈 상태 */
const EmptyState = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
  color: rgba(55, 56, 60, 0.61);
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

export default CoffeeChatHome;